/*
 * Salon state port (v1.0-P2).
 * The one persistence seam the shared SalonApplication depends on. It hands out the full Salon working set and
 * takes it back after a business operation; nothing in the application layer knows whether the set came from the
 * Showcase JSON profile or from PostgreSQL context repositories.
 *
 *   Showcase   → ShowcaseStore            (JSON file, deterministic seed, reset)
 *   Production → PostgresSalonStateStore  (context repositories, one transaction per replace, revision-checked)
 *
 * `load()` returns a fresh clone every time; callers may mutate it freely and hand it to `replace()`.
 * `replace()` persists the whole working set atomically or throws; it never leaves a partial write behind.
 */
import type { SalonSnapshot } from './contracts.js';

export interface SalonStatePort {
  load(): Promise<SalonSnapshot>;
  replace(snapshot: SalonSnapshot): Promise<SalonSnapshot>;
  /* Showcase only: returns the deterministic seed. Production stores throw SHOWCASE_RESET_UNAVAILABLE. */
  reset(confirmed: boolean): Promise<SalonSnapshot>;
}

/*
 * Business operations are load → mutate → replace. Two overlapping operations would otherwise race on the same
 * working set (the second replace would silently drop the first one's change), so every public method of the
 * application runs through one FIFO queue. A method that calls another method of the same instance (e.g.
 * completeCheckout → getCheckoutDetail) is already inside the queued operation and runs inline — detected through
 * AsyncLocalStorage, so nested calls can never deadlock the queue. Reads are cheap in both runtimes (in-memory
 * working set), so the queue costs nothing noticeable and removes an entire class of lost-update bugs.
 */
export function serializeMethods(target: object & { queue: Promise<unknown> }, stopAt: object = Object.prototype): void {
  /* A single application instance owns one queue. Keeping the nested-call marker in this closure is browser-safe
   * and is also sufficient in Node: no second top-level operation can enter while the queued promise is running. */
  let insideOperation = false;
  const names = new Set<string>();
  for (let prototype = Object.getPrototypeOf(target) as Record<string, unknown> | null; prototype && prototype !== stopAt; prototype = Object.getPrototypeOf(prototype) as Record<string, unknown> | null) {
    for (const name of Object.getOwnPropertyNames(prototype)) if (name !== 'constructor' && typeof prototype[name] === 'function') names.add(name);
  }
  for (const name of names) {
    const original = (target as unknown as Record<string, (...args: unknown[]) => unknown>)[name]!;
    Object.defineProperty(target, name, {
      configurable: true,
      writable: true,
      value: (...args: unknown[]) => {
        if (insideOperation) return original.apply(target, args);
        const run = target.queue.then(async () => {
          insideOperation = true;
          try { return await original.apply(target, args); }
          finally { insideOperation = false; }
        });
        target.queue = run.catch(() => undefined);
        return run;
      }
    });
  }
}
